import {BaseType} from "../../types/Enums";

interface BuildingModel {
    model: {
        structure?: {
            components?: Record<string, any>;
        };
    };
}

/**
 * Checks if a surface component belongs to a given room.
 */
export const isComponentInRoom = (building: BuildingModel, surfaceKey: string, roomKey: string): boolean => {
    const surface = building.model.structure?.components?.[surfaceKey];
    return surface?.roomKey === roomKey;
};

/**
 * Generic filter for components that:
 *  - match a specific BaseType
 *  - include the roomKey in `parentRoomKeys.value`
 */
export const filterByParentRoomKeys = (
    building: BuildingModel,
    roomKey: string,
    type: BaseType
): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];

    return Object.values(comps).filter((c: any) =>
        c?.type === type && c?.parentRoomKeys?.value?.some((k: string) => k === roomKey)
    );
};

/**
 * Utilities are modeled as components with type Utility and `parentRoomKeys` membership.
 */
export const filterUtilitiesInRoom = (building: BuildingModel, roomKey: string): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];
    return Object.values(comps).filter((c: any) =>
        c?.type === BaseType.Utility && c?.parentRoomKeys?.value?.includes(roomKey)
    );
};

/**
 * Windows/Doors (openings) exist on parent surfaces. Keep those that:
 *  - match the given type (Window or Door)
 *  - have blueprintLocations
 *  - and at least one parent surface belongs to this room
 */
export const filterOpeningsWithBlueprintInRoom = (
    building: BuildingModel,
    roomKey: string,
    type: BaseType.Window | BaseType.Door
): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];

    return Object.values(comps).filter((c: any) => {
        if (c?.type !== type || !c?.blueprintLocations) return false;
        
        // Check all parentSurface fields
        const parentSurfaces = [
            c?.parentSurface?.value,
            c?.parentSurface1?.value,
            c?.parentSurface2?.value
        ].filter(Boolean);

        return parentSurfaces.some(surfaceKey => 
            surfaceKey && isComponentInRoom(building, surfaceKey, roomKey)
        );
    });
};

/**
 * Surface devices (e.g., outlets, switches, light fixtures) exist on parent surfaces.
 * Keep those that:
 *  - match the given type
 *  - have blueprintLocations
 *  - and at least one parent surface belongs to the room
 */
export const filterSurfaceDevicesWithBlueprintInRoom = (
    building: BuildingModel,
    roomKey: string,
    type: BaseType
): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];

    return Object.values(comps).filter((c: any) => {
        if (c?.type !== type || !c?.blueprintLocations) return false;
        
        // Check all parentSurface fields
        const parentSurfaces = [
            c?.parentSurface?.value,
            c?.parentSurface1?.value,
            c?.parentSurface2?.value
        ].filter(Boolean);

        return parentSurfaces.some(surfaceKey => 
            surfaceKey && isComponentInRoom(building, surfaceKey, roomKey)
        );
    });
};

/**
 * Windows/Doors (openings) exist on parent surfaces. Keep those that:
 *  - match the given type (Window or Door)
 *  - and at least one parent surface belongs to this room
 *  - (blueprintLocations not required)
 */
export const filterOpeningsInRoom = (
    building: BuildingModel,
    roomKey: string,
    type: BaseType.Window | BaseType.Door
): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];

    return Object.values(comps).filter((c: any) => {
        if (c?.type !== type) return false;
        
        // Check all parentSurface fields
        const parentSurfaces = [
            c?.parentSurface?.value,
            c?.parentSurface1?.value,
            c?.parentSurface2?.value
        ].filter(Boolean);

        return parentSurfaces.some(surfaceKey => 
            surfaceKey && isComponentInRoom(building, surfaceKey, roomKey)
        );
    });
};

/**
 * Surface devices (e.g., outlets, switches, light fixtures) exist on parent surfaces.
 * Keep those that:
 *  - match the given type
 *  - and at least one parent surface belongs to the room
 *  - (blueprintLocations not required)
 */
export const filterSurfaceDevicesInRoom = (
    building: BuildingModel,
    roomKey: string,
    type: BaseType
): any[] => {
    const comps = building.model.structure?.components;
    if (!comps) return [];

    return Object.values(comps).filter((c: any) => {
        if (c?.type !== type) return false;
        
        // Check all parentSurface fields
        const parentSurfaces = [
            c?.parentSurface?.value,
            c?.parentSurface1?.value,
            c?.parentSurface2?.value
        ].filter(Boolean);

        return parentSurfaces.some(surfaceKey => 
            surfaceKey && isComponentInRoom(building, surfaceKey, roomKey)
        );
    });
};
